import React, { FunctionComponent, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import moment from 'moment'
import { CampaignPlatformTypeEnum, FeedDetails } from 'Types/index'
import {
  Mobile,
  Desktop,
  DesktopLarge,
  DesktopUntilLarge,
} from 'Shared/components/Responsive'

import { getUserId } from 'Redux/selectors/auth'
import {
  getIsLimitReached,
  getFeedDetails as getFeedDetailsSelector,
} from 'Redux/selectors/feed'

import { calculateDaysLeft } from 'Constants/functions'
import { capitalizeFirstLetter } from 'Helpers/layoutHelpers'

import { ProgressBarComponent } from '../ProgressBar'
import { TrackMatches } from '../TrackMatches'
import { IframeSkeleton } from '../IframeSkeleton'

import { TrackListItemProps } from './props'
import {
  TrackListItemContainer,
  ContentContainer,
  TrackPreview,
  ContentRow,
  ContentColumn,
  ButtonsContainer,
  Button,
  ButtonSimple,
  LimitReachedPlaceholder,
} from './styles'

export const TrackListItem: FunctionComponent<TrackListItemProps> = ({
  track,
  openSkipModal,
  openReviewModal,
}) => {
  const { t } = useTranslation()
  const [isSkeletonVisible, setIsSkeletonVisible] = useState(true)
  const userId = useSelector(getUserId)
  const isLimitReached = useSelector(getIsLimitReached)
  const reviewDetails: FeedDetails = useSelector(getFeedDetailsSelector)

  const {
    id,
    potential_reward,
    rank,
    matched_percent,
    campaign: {
      id: campaign_id,
      start_date,
      end_date,
      track_name,
      artist_name,
      track_url,
      matchedGenres,
      matchedPlaylists,
    },
  } = track

  const daysLeftMemo = useMemo(() => calculateDaysLeft(end_date), [end_date])

  const getTrackId = (url: string) => {
    if (!url) return ''
    const urlParts = url.split('/')
    return urlParts[urlParts.length - 1]
  }

  const matchedGenresCapitalized = useMemo(
    () => matchedGenres.map(el => capitalizeFirstLetter(el)),
    [matchedGenres]
  )

  return (
    <TrackListItemContainer>
      <ContentContainer>
        <ContentRow>
          <TrackPreview>
            {isSkeletonVisible && <IframeSkeleton />}
            <iframe
              title="spotify"
              allow="encrypted-media"
              loading="lazy"
              frameBorder="0"
              height="100%"
              src={`https://open.spotify.com/embed/track/${getTrackId(
                track_url
              )}`}
              width="100%"
              style={{ opacity: Number(!isSkeletonVisible) }}
              onLoad={() => setIsSkeletonVisible(false)}
            />
          </TrackPreview>
          <DesktopLarge>
            <ContentColumn>
              <TrackMatches
                matchedList={matchedGenresCapitalized}
                matchedRate={matched_percent}
                isGenres
              />
              <TrackMatches
                matchedList={matchedPlaylists}
                matchedRate={matchedPlaylists?.length}
              />
            </ContentColumn>
          </DesktopLarge>
          <ProgressBarComponent startDate={start_date} endDate={end_date} />
          <Desktop>
            <ButtonsContainer>
              {isLimitReached ? (
                <LimitReachedPlaceholder>
                  Limited for today
                </LimitReachedPlaceholder>
              ) : (
                <>
                  <Button onClick={() => openReviewModal({ campaign_id })}>
                    Review
                  </Button>
                  <ButtonSimple
                    onClick={() =>
                      openSkipModal({ campaign_id, review_id: id })
                    }
                  >
                    Skip track
                  </ButtonSimple>
                </>
              )}
            </ButtonsContainer>
          </Desktop>
        </ContentRow>
        <ContentRow>
          <DesktopUntilLarge>
            <TrackMatches
              matchedList={matchedGenresCapitalized}
              matchedRate={matched_percent}
              isGenres
            />
            <TrackMatches
              matchedList={matchedPlaylists}
              matchedRate={matchedPlaylists?.length}
            />
          </DesktopUntilLarge>
          <Mobile>
            <TrackMatches
              matchedList={matchedGenresCapitalized}
              matchedRate={matched_percent}
              isGenres
            />
            <TrackMatches
              matchedList={matchedPlaylists}
              matchedRate={matchedPlaylists?.length}
            />
          </Mobile>
          <Mobile>
            <ButtonsContainer>
              {isLimitReached ? (
                <LimitReachedPlaceholder>
                  Limited for today
                </LimitReachedPlaceholder>
              ) : (
                <>
                  <Button onClick={() => openReviewModal({ campaign_id })}>
                    Review
                  </Button>
                  <ButtonSimple
                    onClick={() =>
                      openSkipModal({ campaign_id, review_id: id })
                    }
                  >
                    Skip track
                  </ButtonSimple>
                </>
              )}
            </ButtonsContainer>
          </Mobile>
        </ContentRow>
      </ContentContainer>
    </TrackListItemContainer>
  )
}
